import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Award, ArrowLeft, BookOpen, FileText, ExternalLink } from 'lucide-react';
import api from '../api';

/**
 * Reading-only Module Viewer.
 *
 * All video content was removed per product direction — modules are now
 * "read the training manual + take the quiz". The shared axios `api`
 * client already injects the JWT so we inherit auth automatically.
 */
const ModuleViewer = ({ user, onLogout }) => {
  const { moduleId } = useParams();
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await api.get(`/modules/${moduleId}`);
        if (!cancelled) setModule(response.data);
      } catch (err) {
        console.error('Error fetching module:', err);
        alert('Module not found');
        navigate('/dashboard');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [moduleId, navigate]);

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  if (!module) return null;

  const manualUrl = module.manual_url || '';

  return (
    <div className="module-viewer" data-testid="module-viewer">
      <header style={{ background: '#0b2545', color: 'white', padding: '14px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/dashboard" style={{ color: 'white', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
          <ArrowLeft size={16} /> Back to Dashboard
        </Link>
        <span style={{ fontSize: '0.85rem', opacity: 0.8 }}>{user?.full_name}</span>
        {onLogout && (
          <button onClick={onLogout} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: 'white', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>
            Logout
          </button>
        )}
      </header>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '26px 24px', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 18 }}>
        {/* Reading brief / module content */}
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22 }}>
          <span style={{ display: 'inline-block', background: '#eff6ff', color: '#1e40af', padding: '4px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 0.5, marginBottom: 10 }}>
            MODULE {module.module_number || ''}
          </span>
          <h1 style={{ margin: '0 0 8px', color: '#0b2545', fontSize: '1.4rem' }} data-testid="module-title">{module.title}</h1>
          <p style={{ margin: 0, color: '#334155', fontSize: '0.95rem', lineHeight: 1.65, whiteSpace: 'pre-line' }}>
            {module.reading_brief || module.description}
          </p>

          {Array.isArray(module.objectives) && module.objectives.length > 0 && (
            <div style={{ marginTop: 16, background: '#f8fafc', padding: 14, borderRadius: 8 }}>
              <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 0.6, color: '#0b2545', fontWeight: 700, marginBottom: 8 }}>
                Learning Objectives
              </div>
              <ul style={{ margin: 0, paddingLeft: 18, color: '#475569', fontSize: '0.88rem', lineHeight: 1.6 }}>
                {module.objectives.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </div>
          )}

          <div style={{ marginTop: 18, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link
              to={`/test/${moduleId}`}
              data-testid="start-module-quiz-btn"
              style={{ background: '#22c55e', color: 'white', padding: '12px 20px', borderRadius: 10, fontWeight: 700, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Award size={16} /> Start Module Quiz
            </Link>
            {manualUrl && (
              <a
                href={manualUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ background: '#f1f5f9', color: '#0b2545', padding: '12px 20px', borderRadius: 10, fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <ExternalLink size={14} /> Open Manual (full tab)
              </a>
            )}
          </div>
        </div>

        {/* Training Manual panel */}
        <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', minHeight: 520 }}>
          <h3 style={{ margin: '0 0 10px', color: '#0b2545', fontSize: '1rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <FileText size={18} /> Training Manual
          </h3>
          {manualUrl ? (
            <iframe
              src={`${manualUrl}#toolbar=1&navpanes=0`}
              title="Training Manual"
              data-testid="module-manual-iframe"
              style={{ flex: 1, width: '100%', border: '1px solid #e5e7eb', borderRadius: 8, minHeight: 460 }}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '0.88rem' }}>
              No manual linked for this module. Use the "Back to Dashboard" link to try a different course.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModuleViewer;
